
/* ==========================================================================
   SPATIAL & COMPATIBILITY EVALUATION ENGINE (/database/compatibility-engine.js)
   ========================================================================== */

import { state, clearAllSelectedComponents, getCaseModeSelection, getCaseStateKey, getExactSelectedComponentSpecs, normalizeCaseIdentityName } from './state.js?v=20260819-audit2';
import { rebuildCaseLookupIndexes } from './js/runtime-index.js?v=20260819-audit2';
import { debugRuntimeIssue, reportRuntimeIssueOnce } from './runtime.js?v=20260819-audit2';
import { runtimeNow, debugPerformanceMetric, scheduleIdleTask } from './js/runtime-perf.js?v=20260819-audit2';
import { syncPcieSlotsFromThickness, updateSliderBadges, clearCardPool } from './ui-core.js?v=20260819-audit2';
import { rebuildCatalogIdentityIndex } from './catalog-identity.js?v=20260819-audit2';

const casesStructuredMap = new Map();
const casesStructuredNameMap = new Map();

const RULE_OVERRIDE_LIMITS = Object.freeze({
  maxGPU: [80, 500], maxGPUH: [40, 250], maxGPUT: [10, 160],
  maxCPU: [10, 250], maxAIO: [0, 480], maxPSU: [40, 300]
});

// Legacy structured overlays used numeric 0 as a sentinel for "not specified / no override"
// on dimensional fields where a literal zero is not a usable physical clearance. Preserve
// strict validation for real values without flooding the console for those legacy sentinels.
const ZERO_MEANS_UNSET_OVERRIDE_FIELDS = new Set(['maxGPUH', 'maxGPUT', 'maxCPU', 'maxPSU']);
const RULE_CONDITION_KEYS = new Set([
  'cooling', 'rad', 'radiator', 'radFan', 'mobo', 'psu',
  'gpuT_gt', 'gpuT_gte', 'gpuT_lt', 'gpuT_lte',
  'cpuH_gt', 'cpuH_gte', 'cpuH_lt', 'cpuH_lte',
  'gpuH_gt', 'gpuH_gte', 'gpuH_lt', 'gpuH_lte',
  'gpuL_gt', 'gpuL_gte', 'gpuL_lt', 'gpuL_lte'
]);

function slugKey(value) {
  return String(value || '').trim().toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

function deriveStableCaseKey(caseObj) {
  return getCaseStateKey(caseObj);
}

function finiteNumberOrNull(value) {
  if (value === null || value === undefined || value === '') return null;
  if (typeof value === 'number') return Number.isFinite(value) ? value : null;
  const match = String(value).trim().match(/-?\d+(?:\.\d+)?/);
  if (!match) return null;
  const num = Number(match[0]);
  return Number.isFinite(num) ? num : null;
}


const RULE_CONDITION_BOUNDS = Object.freeze({ gpuT: [0, 200], cpuH: [0, 300], gpuH: [0, 300], gpuL: [0, 600] });

function validateRuleConditions(raw, contextLabel) {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw) || !Object.keys(raw).length) return null;
  const clean = {};
  for (const [key, value] of Object.entries(raw)) {
    if (!RULE_CONDITION_KEYS.has(key)) {
      reportRuntimeIssueOnce(`rule-condition:${contextLabel}:${key}`, 'Rules', new Error(`Rejected unsupported condition '${key}' in ${contextLabel}`));
      return null;
    }
    // Older structured overlays can contain every optional schema key with null values.
    // Null means "condition not specified", not "unsupported condition". Ignore it so
    // legacy overlays behave like the current parser's cleaned condition objects.
    if (value === null || value === undefined) continue;
    if (key === 'rad' || key === 'radiator') {
      const num = finiteNumberOrNull(value);
      if (num === null || num < 0 || num > 480) return null;
      clean[key] = num; continue;
    }
    if (key === 'cooling') {
      const normalized = String(value).trim().toLowerCase();
      if (!['air', 'liquid', 'aio', 'water'].includes(normalized)) return null;
      clean[key] = normalized; continue;
    }
    if (key === 'radFan') {
      const normalized = String(value).trim().toLowerCase();
      if (!['slim', 'standard', 'thick', '30mm'].includes(normalized)) return null;
      clean[key] = normalized; continue;
    }
    if (key === 'psu') {
      const normalized = String(value).trim().toUpperCase().replace(/_/g, '-').replace('FLEX-ATX', 'FLEX');
      if (!['SFX', 'SFX-L', 'ATX', 'FLEX', 'DC'].includes(normalized)) return null;
      clean[key] = normalized; continue;
    }
    if (key === 'mobo') {
      const values = (Array.isArray(value) ? value : [value]).map(v => String(v).trim().toUpperCase().replace('MICRO-ATX', 'MATX').replace('M-ATX', 'MATX'));
      if (!values.length || values.some(v => !['ITX', 'DTX', 'MATX', 'ATX', 'EATX', 'E-ATX'].includes(v))) return null;
      clean[key] = values; continue;
    }
    const suffix = ['_gte', '_lte', '_gt', '_lt'].find(candidate => key.endsWith(candidate));
    const field = suffix ? key.slice(0, -suffix.length) : '';
    const bounds = RULE_CONDITION_BOUNDS[field];
    const num = finiteNumberOrNull(value);
    if (!suffix || !bounds || num === null || num < bounds[0] || num > bounds[1]) return null;
    clean[key] = num;
  }
  return Object.keys(clean).length ? clean : null;
}

function validateOverrideObject(raw, contextLabel) {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return null;
  const clean = {};
  for (const [field, bounds] of Object.entries(RULE_OVERRIDE_LIMITS)) {
    if (raw[field] === null || raw[field] === undefined) continue;
    const value = Number(raw[field]);

    // maxAIO=0 is a real constraint (no liquid-cooling support), so it is intentionally
    // NOT included here. For the fields below, zero in legacy overlays means "unset".
    if (value === 0 && ZERO_MEANS_UNSET_OVERRIDE_FIELDS.has(field)) continue;

    if (!Number.isFinite(value) || value < bounds[0] || value > bounds[1]) {
      reportRuntimeIssueOnce(`rule-override:${contextLabel}:${field}`, 'Rules', new Error(`Rejected invalid ${field}=${raw[field]} in ${contextLabel}`));
      continue;
    }
    clean[field] = value;
  }
  return Object.keys(clean).length ? clean : null;
}

function validateFixedStepList(rawSteps, contextLabel) {
  if (!Array.isArray(rawSteps)) return [];
  return rawSteps.map((step, index) => {
    const maxCPU = finiteNumberOrNull(step?.maxCPU);
    const maxThick = finiteNumberOrNull(step?.maxThick ?? step?.maxGPUT);
    if (maxCPU === null || maxThick === null || maxCPU < 10 || maxCPU > 250 || maxThick < 10 || maxThick > 160) {
      reportRuntimeIssueOnce(`fixed-step:${contextLabel}:${index}`, 'Rules', new Error(`Rejected invalid fixed spine step for ${contextLabel}`));
      return null;
    }
    return { ...step, slotName: String(step?.slotName || step?.id || `Step ${index + 1}`), maxCPU, maxThick };
  }).filter(Boolean);
}

function validateStructuredEntry(entry) {
  if (!entry || typeof entry !== 'object') return null;
  const clean = { ...entry };
  const seenRuleIds = new Set();
  const rawModes = globalThis.SFFContracts?.expandCaseModes?.(entry.modes)
    || (Array.isArray(entry.modes) ? entry.modes : []);

  clean.modes = rawModes.map((mode, index) => {
    if (!mode || typeof mode !== 'object') return null;
    const modeId = String(mode.mode_id || mode.id || mode.mode_name || `mode-${index}`).trim();
    if (!modeId || modeId.length > 120) {
      reportRuntimeIssueOnce(`mode-id:${entry.name}:${index}`, 'Rules', new Error(`Rejected invalid mode id for ${entry.name || 'case'}`));
      return null;
    }
    const overrides = validateOverrideObject(mode.overrides || {}, `${entry.name || entry._case_key || 'case'} mode ${modeId}`);
    const localSteps = validateFixedStepList(mode.fixedSteps, `${entry.name || entry._case_key || 'case'} mode ${modeId}`);
    return {
      ...mode,
      mode_id: modeId,
      overrides: overrides || {},
      ...(Array.isArray(mode.fixedSteps) ? { fixedSteps: localSteps } : {})
    };
  }).filter(Boolean);

  clean.fixedSteps = validateFixedStepList(entry.fixedSteps, entry.name || entry._case_key || 'case');

  clean.rules = Array.isArray(entry.rules) ? entry.rules.map((rule, index) => {
    if (!rule || typeof rule !== 'object') return null;
    const ruleId = slugKey(rule.rule_id || `rule-${index}`);
    if (!ruleId || seenRuleIds.has(ruleId)) {
      reportRuntimeIssueOnce(`duplicate-rule:${entry.name}:${ruleId}`, 'Rules', new Error(`Duplicate/invalid rule id '${ruleId}' for ${entry.name || 'case'}`));
      return null;
    }
    seenRuleIds.add(ruleId);

    const conditions = validateRuleConditions(rule.conditions || rule.when, `${entry.name || entry._case_key || 'case'}:${ruleId}`);
    if (!conditions) return null;

    const overrides = validateOverrideObject(rule.overrides || {}, `${entry.name || 'case'} rule ${ruleId}`);
    const failMessage = rule.fail_message || rule.message || rule.error || null;
    const warnMessage = rule.warn_message || null;
    if (overrides && warnMessage && !failMessage) {
      reportRuntimeIssueOnce(`advisory-override:${entry.name}:${ruleId}`, 'Rules', new Error(`Rejected dimensional overrides on advisory-only rule ${ruleId}`));
      return { ...rule, rule_id: ruleId, conditions, overrides: null, fail_message: failMessage, warn_message: warnMessage };
    }
    return { ...rule, rule_id: ruleId, conditions, overrides, fail_message: failMessage, warn_message: warnMessage };
  }).filter(Boolean) : [];

  clean._case_key = deriveStableCaseKey(entry);
  return clean;
}

export function getCaseLayoutTag(c) {
  const lo = String(c._layout || c.layout || 'reference').toLowerCase();
  const isVertical = c._dims && c._dims[2] > c._dims[0];

  if (lo === 'apu') return c._maxGPU > 0 ? (isVertical ? 'VERT-APU-LP' : 'APU-LP') : 'APU';
  if (lo === 'console') return 'CONSOLE';
  if (lo === 'sandwich') return isVertical ? 'VERT-SANDWICH' : 'SANDWICH';
  if (lo === 'reference') return isVertical ? 'VERT-REFERENCE' : 'REFERENCE';
  return lo.toUpperCase();
}

async function fetchCasesStructuredDataset(datasetVersion = '') {
  const versionSuffix = datasetVersion ? `?v=${encodeURIComponent(datasetVersion)}` : '';
  const res = await fetch(`/cases_structured.json${versionSuffix}`, { cache: datasetVersion ? 'default' : 'no-cache' });
  if (!res.ok) throw new Error(`HTTP ${res.status} loading cases_structured.json`);
  return res.json();
}

function applyCasesStructuredDataset(dataset) {
  casesStructuredMap.clear();
  casesStructuredNameMap.clear();

  if (Array.isArray(dataset)) {
    dataset.forEach(rawItem => {
      const item = validateStructuredEntry(rawItem);
      if (!item) return;
      if (item._case_key) casesStructuredMap.set(item._case_key, item);
      if (item.name) casesStructuredNameMap.set(normalizeCaseIdentityName(item.name), item);
    });
  }

  if (!Array.isArray(state.fetchedCasesGlobal)) return;
  state.fetchedCasesGlobal.forEach(c => {
    const stableKey = deriveStableCaseKey(c);
    const exactName = normalizeCaseIdentityName(c.name || '');
    const match = casesStructuredMap.get(stableKey) || casesStructuredNameMap.get(exactName);
    if (!match) return;
    if (Array.isArray(match.modes) && match.modes.length > 0) c.modes = match.modes;
    if (Array.isArray(match.rules) && match.rules.length > 0) c.rules = match.rules;
    if (Array.isArray(match.fixedSteps) && match.fixedSteps.length > 0) c.fixedSteps = match.fixedSteps;
    if (typeof match.hasSpine === 'boolean') c.hasSpine = match.hasSpine;
    c._structuredCaseKey = match._case_key;
  });
}

export async function loadCasesStructuredRules(datasetVersion = '') {
  try {
    applyCasesStructuredDataset(await fetchCasesStructuredDataset(datasetVersion));
  } catch (err) {
    console.error('[Compatibility Engine] Failed to load cases_structured.json:', err);
  }
}

export const STRUCTURAL_RULES = [
  {
    condition: (f) => (f.mobo === "MATX" && f.psu === "ATX"),
    keywords: [["m-atx", "matx"], ["atx psu", "atx power", "large power"]],
    error: "COMBINED MATX BOARD AND ATX PSU EXCEEDS INTERNAL CASE CLEARANCES"
  },
  {
    condition: (f) => (f.cooling === "liquid" && f.rad === 280),
    keywords: [["280mm"], ["clearance conflict", "limitation", "restriction", "interference"]],
    error: "CASE SPEC NOTES CONFIRM 280MM RADIATOR FITMENT INTERFERENCE"
  }
];

export function normalizeList(raw) {
  if (!raw) return [];
  let arr = []; 
  if (Array.isArray(raw)) arr = raw; 
  else if (typeof raw === 'string') arr = raw.split(/[\/,|]+/); 
  else arr = [String(raw)];
  return arr.map(s => String(s).trim().toUpperCase()).filter(Boolean);
}

export function enrichCase(c) {
  try {
    c._lcName = String(c.name || '').toLowerCase();
    c._slug = slugKey(c.case_slug || c.db_slug || c.slug || c.name || '');
    const st = String(c.status || '').toLowerCase();
    const lo = String(c.layout || 'reference').toLowerCase();
    c._isLegacy = st.includes('discontinued') || st.includes('legacy') || st.includes('eol');
    c._stableKey = deriveStableCaseKey(c);

    const rawMaxGpu = c.maxGPU ?? c._maxGPU;
    const rawMaxCpu = c.maxCPU ?? c._maxCPU;
    const rawMaxThick = c.maxThick ?? c._maxThick;
    const rawMaxGpuH = c.maxGPUHeight ?? c._maxGPUH;
    const rawMaxAio = c.maxAIO ?? c._maxAIO;
    const rawPcie = c.pcieSlots ?? c._pcie;

    const parsedMaxGpu = finiteNumberOrNull(rawMaxGpu);
    const parsedMaxCpu = finiteNumberOrNull(rawMaxCpu);
    const parsedMaxThick = finiteNumberOrNull(rawMaxThick);
    const parsedMaxGpuH = finiteNumberOrNull(rawMaxGpuH);
    const parsedMaxAio = finiteNumberOrNull(rawMaxAio);
    const parsedPcie = finiteNumberOrNull(rawPcie);
    const parsedVolume = finiteNumberOrNull(c.volume ?? c._volume);

    c._known = {
      maxGPU: parsedMaxGpu !== null,
      maxCPU: parsedMaxCpu !== null,
      maxGPUT: parsedMaxThick !== null,
      maxGPUH: parsedMaxGpuH !== null,
      maxAIO: parsedMaxAio !== null,
      pcie: parsedPcie !== null
    };

    c._maxGPU = parsedMaxGpu ?? 0;
    c._maxCPU = parsedMaxCpu ?? 0;
    c._maxThick = parsedMaxThick ?? 0;
    c._maxGPUH = parsedMaxGpuH ?? 0;
    c._maxAIO = parsedMaxAio ?? 0;
    c._volume = parsedVolume ?? 0;
    c._pcie = parsedPcie ?? 0;
    c._layout = lo;

    // Missing GPU data is UNKNOWN, not an implicit APU-only declaration.
    // APU status must come from an explicit APU layout and an explicit zero/no-GPU capability.
    const explicitNoGpu = c._known.maxGPU && c._maxGPU === 0;
    const explicitApuFlag = c.apu_only === true || c.apuOnly === true || String(c.gpuSupport || '').toLowerCase() === 'none';
    c._isAPU = lo === 'apu' && (explicitNoGpu || explicitApuFlag);

    const d = String(c.caseDimensions || '').toLowerCase().replace(/mm/g, '').split(/\s*[x×X]\s*/).map(v => finiteNumberOrNull(v) ?? 0);
    c._dims = [d[0] || 0, d[1] || 0, d[2] || 0];

    c._moboList = normalizeList(c.mobo); c._moboSet = new Set(c._moboList);
    c._psuList = normalizeList(c.psu); c._psuSet = new Set(c._psuList);
    c._specText = c.specNotes ? Object.values(c.specNotes).join(' ').toLowerCase() : '';
    c.notes = c.notes || c.notes_and_warnings || c.special_notes || c.remarks || c.caveats || '';
    c._priceVal = parseFloat(c.priceUSD) || 0;
    if (!c.image) c.image = `/assets/images/${String(c.name || 'case').replace(/[^a-z0-9]/gi, '-').toLowerCase().replace(/-+/g, '-')}.webp`;
  } catch (e) {
    debugRuntimeIssue('Case Enrichment', e);
  }
  return c;
}

export function getActiveModeObject(c, activeModeId) {
  if (!c || !Array.isArray(c.modes) || c.modes.length === 0) return null;

  const requested = String(activeModeId || 'default').trim();
  const sharedMatch = globalThis.SFFContracts?.resolveModeRecord?.(
    c.modes,
    requested,
    { fallbackDefault: !requested || requested.toLowerCase() === 'default' }
  );
  if (sharedMatch) return sharedMatch;

  const normalizedMode = requested.toLowerCase();
  const matched = c.modes.find(m =>
    String(m?.mode_id || '').toLowerCase() === normalizedMode ||
    String(m?.mode_name || '').toLowerCase() === normalizedMode
  );
  if (matched) return matched;

  if ((normalizedMode === 'default' || !activeModeId) && c.modes.length > 0) return c.modes[0];
  return null;
}

export function getEffectiveSpecs(c, activeModeId = 'default') {
  const baseKnown = { ...(c._known || {}) };
  const parsedMaxPsu = finiteNumberOrNull(c.maxPSU);
  const base = {
    maxGPU: c._maxGPU ?? 0,
    maxGPUH: c._maxGPUH ?? 0,
    maxGPUT: c._maxThick ?? 0,
    maxCPU: c._maxCPU ?? 0,
    maxAIO: c._maxAIO ?? 0,
    maxPSU: parsedMaxPsu ?? 0,
    known: {
      maxGPU: !!baseKnown.maxGPU,
      maxGPUH: !!baseKnown.maxGPUH,
      maxGPUT: !!baseKnown.maxGPUT,
      maxCPU: !!baseKnown.maxCPU,
      maxAIO: !!baseKnown.maxAIO,
      maxPSU: parsedMaxPsu !== null,
      pcie: !!baseKnown.pcie
    }
  };

  const activeMode = getActiveModeObject(c, activeModeId);
  if (!activeMode || !activeMode.overrides) return base;

  const output = { ...base, known: { ...base.known } };
  const mapping = { maxGPU: 'maxGPU', maxGPUH: 'maxGPUH', maxGPUT: 'maxGPUT', maxCPU: 'maxCPU', maxAIO: 'maxAIO', maxPSU: 'maxPSU' };
  for (const [field, source] of Object.entries(mapping)) {
    if (activeMode.overrides[source] !== undefined && activeMode.overrides[source] !== null) {
      output[field] = Number(activeMode.overrides[source]);
      output.known[field] = true;
    }
  }
  return output;
}

export function hasPsuFast(c, needle) {
  const n = needle.toUpperCase();
  if (n === "FLEX") return c._psuSet.has("FLEX") || [...c._psuSet].some(p => p.includes("FLEX"));
  if (n === "ATX") return c._psuSet.has("ATX") || [...c._psuSet].some(p => p === "ATX" || (p.includes("ATX") && !p.includes("FLEX") && !p.includes("DC")));
  if (n === "DC-ATX" || n === "DC") return c._psuSet.has("DC") || [...c._psuSet].some(p => p.includes("DC"));
  return c._psuSet.has(n) || [...c._psuSet].some(p => p.includes(n));
}

function normalizeMotherboardToken(value) {
  const raw = String(value || '').trim().toUpperCase().replace(/[_\s]+/g, '-');
  if (raw === 'MINI-ITX' || raw === 'MINIITX') return 'ITX';
  if (raw === 'M-ATX' || raw === 'MICRO-ATX' || raw === 'MICROATX') return 'MATX';
  if (raw === 'E-ATX' || raw === 'EXTENDED-ATX' || raw === 'EXTENDEDATX') return 'EATX';
  return raw;
}

export function hasMoboFast(c, needle) {
  const requested = normalizeMotherboardToken(needle);
  const supported = new Set([...(c?._moboSet || [])].map(normalizeMotherboardToken).filter(Boolean));

  // Never let ATX match MATX by substring. E-ATX remains a genuine ATX superset.
  if (requested === 'ATX') return supported.has('ATX') || supported.has('EATX');
  return supported.has(requested);
}

function parseCleanNumber(val) {
  if (val === null || val === undefined) return NaN;
  const cleaned = String(val).replace(/[^0-9.]/g, '');
  return cleaned === '' ? NaN : Number(cleaned);
}

function matchRuleConditions(conditions, f) {
  if (!conditions || typeof conditions !== 'object') return false;

  let matchedAny = false;

  for (const [key, expected] of Object.entries(conditions)) {
    if (expected === null || expected === undefined) continue;

    matchedAny = true;

    if (key === 'cooling') {
      const current = String(f.cooling || '').toLowerCase();
      const target = String(expected).toLowerCase();
      const isLiquidMatch = (target === 'liquid' || target === 'aio') && (current.includes('liquid') || current.includes('aio') || current.includes('water'));
      const isAirMatch = target === 'air' && current.includes('air');
      if (!isLiquidMatch && !isAirMatch && !current.includes(target)) return false;
      continue;
    }

    if (key === 'rad' || key === 'radiator' || key === 'maxAIO') {
      const currentRad = parseCleanNumber(f.rad || f.radiator || f.selectedRad);
      const expectedRad = parseCleanNumber(expected);
      if (isNaN(currentRad) || currentRad !== expectedRad) return false;
      continue;
    }

    if (key.endsWith('_gt')) {
      const field = key.replace('_gt', '');
      if (!(parseCleanNumber(f[field]) > parseCleanNumber(expected))) return false;
      continue;
    }
    if (key.endsWith('_gte')) {
      const field = key.replace('_gte', '');
      if (!(parseCleanNumber(f[field]) >= parseCleanNumber(expected))) return false;
      continue;
    }
    if (key.endsWith('_lt')) {
      const field = key.replace('_lt', '');
      if (!(parseCleanNumber(f[field]) < parseCleanNumber(expected))) return false;
      continue;
    }
    if (key.endsWith('_lte')) {
      const field = key.replace('_lte', '');
      if (!(parseCleanNumber(f[field]) <= parseCleanNumber(expected))) return false;
      continue;
    }

    const actualVal = f[key];
    if (actualVal !== undefined && actualVal !== null) {
      if (Array.isArray(expected)) {
        const match = expected.some(exp => String(exp).toLowerCase().trim() === String(actualVal).toLowerCase().trim());
        if (!match) return false;
      } else {
        const cleanActual = parseCleanNumber(actualVal);
        const cleanExp = parseCleanNumber(expected);
        
        if (!isNaN(cleanActual) && !isNaN(cleanExp)) {
          if (cleanActual !== cleanExp) return false;
        } else {
          if (String(actualVal).toLowerCase().trim() !== String(expected).toLowerCase().trim()) return false;
        }
      }
      continue;
    }

    return false;
  }

  return matchedAny;
}

function applyDynamicCaseRules(c, f, currentSpecs, fails, warns) {
  let ruleList = c.rules || c.compatibility_rules;

  if (!Array.isArray(ruleList) || !ruleList.length) {
    const stableKey = deriveStableCaseKey(c);
    const exactName = String(c.name || '').toLowerCase().trim();
    const structuredMatch = casesStructuredMap.get(stableKey) || casesStructuredNameMap.get(exactName);
    ruleList = structuredMatch?.rules || structuredMatch?.compatibility_rules || [];
  }

  if (!Array.isArray(ruleList) || ruleList.length === 0) return currentSpecs;

  const updatedSpecs = { ...currentSpecs, known: { ...(currentSpecs.known || {}) } };

  for (const rule of ruleList) {
    const conds = rule.conditions || rule.when;
    if (!matchRuleConditions(conds, f)) continue;

    const failMsg = rule.fail_message || rule.message || rule.error;
    const warnMsg = rule.warn_message;
    const overrides = validateOverrideObject(rule.overrides || {}, `${c.name || 'case'} runtime rule ${rule.rule_id || 'unnamed'}`);

    if (overrides) {
      for (const [field, value] of Object.entries(overrides)) {
        const current = finiteNumberOrNull(updatedSpecs[field]);
        updatedSpecs[field] = current === null || current <= 0 ? value : Math.min(current, value);
        if (updatedSpecs.known) updatedSpecs.known[field] = true;
      }
    }

    const currentGpuT = parseCleanNumber(f.gpuT ?? f.gpuThick ?? f.gpuThickness);
    const currentGpuL = parseCleanNumber(f.gpuL ?? f.gpuLength);
    const currentGpuH = parseCleanNumber(f.gpuH ?? f.gpuHeight);
    const currentCpuH = parseCleanNumber(f.cpuH ?? f.cpuHeight);
    const currentAio = parseCleanNumber(f.rad ?? f.radiator ?? f.selectedRad);
    const currentPsuLength = parseCleanNumber(f.psuL ?? f.psuLength);

    const violations = [
      overrides?.maxGPUT !== undefined && Number.isFinite(currentGpuT) && currentGpuT > updatedSpecs.maxGPUT,
      overrides?.maxGPU !== undefined && Number.isFinite(currentGpuL) && currentGpuL > updatedSpecs.maxGPU,
      overrides?.maxGPUH !== undefined && Number.isFinite(currentGpuH) && currentGpuH > updatedSpecs.maxGPUH,
      overrides?.maxCPU !== undefined && Number.isFinite(currentCpuH) && currentCpuH > updatedSpecs.maxCPU,
      overrides?.maxAIO !== undefined && Number.isFinite(currentAio) && currentAio > updatedSpecs.maxAIO,
      overrides?.maxPSU !== undefined && Number.isFinite(currentPsuLength) && currentPsuLength > updatedSpecs.maxPSU
    ];

    // A fail-only relational rule represents an explicit conflict even without a
    // dimensional override. With overrides, fail only when the current build
    // actually crosses one of the tightened limits.
    if (failMsg && (!overrides || violations.some(Boolean))) {
      if (!fails.includes(failMsg)) fails.push(failMsg);
    }
    if (warnMsg && !warns.includes(warnMsg)) warns.push(warnMsg);
  }

  return updatedSpecs;
}

export function checkCompatibility(c, f, activeModeId = null) {
  let fails = [], warns = [], unknowns = [], advisories = [], spineStep = null;

  const addAdvisory = (code, message, details = {}) => {
    if (!message) return;
    advisories.push({
      code,
      message,
      source: details.source || 'generic_geometry',
      authority: 'advisory',
      ...details
    });
    // Keep the legacy warning surface populated while exposing structured provenance.
    warns.push(message);
  };

  const selectedMode = activeModeId || f.mode || f.selectedModeId || f.modeId || 'default';
  const specs = getEffectiveSpecs(c, selectedMode);

  let maxGpuLen = specs.maxGPU;
  let maxThick = specs.maxGPUT;
  let maxGpuHeight = specs.maxGPUH;
  let maxCpu = specs.maxCPU;
  let maxAio = specs.maxAIO;

  const activeModeObj = getActiveModeObject(c, selectedMode);

  // Resolve fixed steps with the same authority order used by the 3D Workbench:
  // explicit portable fixed step > active-mode implied spine position > hardware-fit AUTO.
  // This matters for catalogues that expose the same physical spine state twice: once as a
  // logical mode (e.g. "3.25-Slot Mode") and again in global/mode-local fixedSteps.
  const modeFixedSteps = activeModeObj && Array.isArray(activeModeObj.fixedSteps) ? activeModeObj.fixedSteps : null;
  const globalFixedSteps = c.hasSpine && Array.isArray(c.fixedSteps) ? c.fixedSteps : null;
  const modeLabelText = activeModeObj
    ? [activeModeObj.mode_id, activeModeObj.mode_name, activeModeObj.name, activeModeObj.slotName].filter(Boolean).join(' ')
    : '';
  const modeExplicitlySpine = !!activeModeObj && (activeModeObj.hasSpine === true || /\d+(?:\.\d+)?\s*[- ]?slot/i.test(modeLabelText));

  let activeFixedSteps = null;
  if (modeFixedSteps && modeFixedSteps.length > 0) {
    activeFixedSteps = modeFixedSteps;
  } else if (activeModeObj) {
    if (modeExplicitlySpine && globalFixedSteps && globalFixedSteps.length > 0) {
      activeFixedSteps = globalFixedSteps;
    } else {
      activeFixedSteps = null; // A non-spine active mode must not inherit global spine states.
    }
  } else if (c.hasSpine && globalFixedSteps) {
    activeFixedSteps = globalFixedSteps;
  }

  if (activeFixedSteps && activeFixedSteps.length > 0) {
    const rawRequestedFixedStep = String(f.fixedStep || f.fixed_step || state.portableFixedStep || '').trim();
    const normalizeSpineKey = value => String(value || '').trim().toLowerCase().replace(/[^a-z0-9.]+/g, '');
    const slotNumber = value => {
      const match = String(value || '').match(/(\d+(?:\.\d+)?)\s*[- ]?slot/i);
      return match ? Number(match[1]) : null;
    };
    const stepKeys = step => [step?.slotName, step?.id, step?.mode_id, step?.name];
    let step = null;

    // 1. Exact imported/documented spine state is authoritative when still present.
    if (rawRequestedFixedStep) {
      const wanted = normalizeSpineKey(rawRequestedFixedStep);
      step = globalThis.SFFContracts?.resolveFixedStepRecord?.(activeFixedSteps, rawRequestedFixedStep)
        || activeFixedSteps.find(candidate => stepKeys(candidate)
          .some(value => normalizeSpineKey(value) === wanted))
        || null;
      if (!step) {
        addAdvisory(
          'requested_spine_step_unavailable',
          `Requested spine position "${rawRequestedFixedStep}" is unavailable in the active case mode; mode/automatic spine resolution was used.`,
          { source: 'portable_build_state' }
        );
      }
    }

    // 2. If the active mode itself names the physical spine state, bind that mode to the
    // matching fixedStep before considering the selected hardware. 3D already behaved this
    // way; /db previously re-picked a different step from GPU/CPU dimensions here.
    if (!step && activeModeObj) {
      const modeKeys = new Set([
        activeModeObj.mode_id, activeModeObj.mode_name, activeModeObj.name, activeModeObj.slotName
      ].map(normalizeSpineKey).filter(Boolean));
      step = activeFixedSteps.find(candidate => stepKeys(candidate)
        .map(normalizeSpineKey).some(key => key && modeKeys.has(key))) || null;

      if (!step) {
        const wantedSlots = [
          activeModeObj.mode_id, activeModeObj.mode_name, activeModeObj.name, activeModeObj.slotName
        ].map(slotNumber).find(value => Number.isFinite(value));
        if (Number.isFinite(wantedSlots)) {
          step = activeFixedSteps.find(candidate => stepKeys(candidate)
            .some(value => slotNumber(value) === wantedSlots)) || null;
        }
      }
    }

    // 3. AUTO is only the fallback for genuinely unspecified spine state.
    if (!step) {
      step = f.cooling === 'air'
        ? activeFixedSteps.find(s => s.maxThick >= f.gpuT && s.maxCPU >= f.cpuH)
        : activeFixedSteps.find(s => s.maxThick >= f.gpuT);
    }

    if (!step) {
      fails.push("HARDWARE LAYOUT CONFLICT IN DYNAMIC SPINE");
      step = activeFixedSteps[activeFixedSteps.length - 1];
    }
    spineStep = step;
    maxCpu = step.maxCPU;
    maxThick = step.maxThick;
  }

  const currentWorkingSpecs = {
    maxGPU: maxGpuLen,
    maxGPUT: maxThick,
    maxGPUH: maxGpuHeight,
    maxCPU: maxCpu,
    maxAIO: maxAio,
    maxPSU: specs.maxPSU,
    known: { ...(specs.known || {}) }
  };
  const constrainedSpecs = applyDynamicCaseRules(c, f, currentWorkingSpecs, fails, warns);

  maxGpuLen = constrainedSpecs.maxGPU;
  maxThick = constrainedSpecs.maxGPUT;
  maxGpuHeight = constrainedSpecs.maxGPUH;
  maxCpu = constrainedSpecs.maxCPU;
  maxAio = constrainedSpecs.maxAIO;

  if (c._moboList.length > 0) {
    if (f.mobo === "ATX" && !hasMoboFast(c, "ATX")) fails.push("MOBO ATX NOT SUPPORTED");
    if (f.mobo === "MATX" && !hasMoboFast(c, "MATX") && !hasMoboFast(c, "ATX")) fails.push("MATX NOT SUPPORTED");
  } else if (f.mobo) {
    unknowns.push('MOTHERBOARD FORM-FACTOR SUPPORT UNKNOWN — VERIFY');
  }

  if (c._psuList.length > 0) {
    if (f.psu === "SFX" && !hasPsuFast(c, "SFX")) fails.push("SFX PSU NOT SUPPORTED");
    if (f.psu === "SFX-L" && !hasPsuFast(c, "SFX-L")) fails.push("SFX-L NOT SUPPORTED");
    if (f.psu === "ATX" && !hasPsuFast(c, "ATX")) fails.push("ATX PSU NOT SUPPORTED");
    if (f.psu === "FLEX" && !hasPsuFast(c, "FLEX")) fails.push("FLEX-ATX NOT SUPPORTED");
    if (f.psu === "DC-ATX" && !hasPsuFast(c, "DC")) fails.push("DC-ATX NOT SUPPORTED");
  } else if (f.psu) {
    unknowns.push('PSU FORM-FACTOR SUPPORT UNKNOWN — VERIFY');
  }

  // Broad volume/layout heuristics are deliberately non-authoritative. They can
  // flag a configuration for manual verification, but may not tighten published
  // case limits or create a hard incompatibility. Only source-grounded catalog
  // specs, modes, fixed steps and structured rules are allowed to populate fails.
  if (f.psu === "ATX" && c._volume < 15 && hasPsuFast(c, "ATX")) {
    if (f.cooling === "liquid" && parseCleanNumber(f.rad) === 280) {
      addAdvisory(
        'GENERIC_ATX_280_SUB15L',
        'ATX PSU + 280MM RADIATOR IN A SUB-15L CASE MAY CREATE A GEOMETRIC CONFLICT — VERIFY CASE-SPECIFIC MOUNTING',
        { source: 'generic_volume_layout', volume_l: c._volume }
      );
    } else if (f.mobo !== "ITX") {
      addAdvisory(
        'GENERIC_ATX_NONITX_SUB15L',
        'ATX PSU WITH mATX/ATX MOTHERBOARD IN A SUB-15L CASE MAY REDUCE GPU OR RADIATOR CLEARANCE — VERIFY CASE-SPECIFIC LIMITS',
        { source: 'generic_volume_layout', volume_l: c._volume }
      );
    } else {
      addAdvisory(
        'GENERIC_ATX_ITX_SUB15L',
        "ATX PSU IN A SUB-15L CASE MAY REDUCE INTERNAL CLEARANCE; VERIFY 240MM+ RADIATOR AND GPU FITMENT",
        { source: 'generic_volume_layout', volume_l: c._volume }
      );
    }
  }

  if (f.psu === "FLEX" && c._dims[0] > 0 && c._dims[0] < 150) {
    addAdvisory(
      'GENERIC_FLEX_ENVELOPE',
      'FLEX-ATX NOMINAL LENGTH MAY EXCEED THE EXTERIOR CASE LENGTH — VERIFY THE INTERNAL PSU ORIENTATION',
      { source: 'generic_chassis_envelope', case_length_mm: c._dims[0] }
    );
  }

  const knownSpecs = constrainedSpecs.known || specs.known || {};
  if (!c._isAPU) {
    if (knownSpecs.maxGPU) {
      if (f.gpuL > maxGpuLen) fails.push("GPU LENGTH EXCEEDED");
    } else if (Number(f.gpuL) > 0) unknowns.push('GPU LENGTH CLEARANCE UNKNOWN — VERIFY');

    if (knownSpecs.maxGPUT) {
      if (f.gpuT > maxThick) fails.push("GPU THICKNESS EXCEEDED");
    } else if (Number(f.gpuT) > 0) unknowns.push('GPU THICKNESS CLEARANCE UNKNOWN — VERIFY');

    if (knownSpecs.maxGPUH) {
      if (f.gpuH > maxGpuHeight) fails.push("GPU HEIGHT EXCEEDED");
    } else if (Number(f.gpuH) > 0) unknowns.push('GPU HEIGHT CLEARANCE UNKNOWN — VERIFY');
  }

  if (f.cooling === 'air') {
    if (knownSpecs.maxCPU) {
      if (f.cpuH > maxCpu) fails.push("CPU CLEARANCE EXCEEDED");
    } else if (Number(f.cpuH) > 0) unknowns.push('CPU COOLER CLEARANCE UNKNOWN — VERIFY');
  }

  if (!c._isAPU && Number(f.pcie) > 0) {
    if (c._known?.pcie && c._pcie > 0) {
      if (Number(f.pcie) > Number(c._pcie)) fails.push(`MAX ${c._pcie} PCIE SLOTS`);
    } else {
      unknowns.push('PCIE EXPANSION SLOT CAPACITY UNKNOWN — VERIFY');
    }
  }

  if (f.cooling === 'liquid' || String(f.cooling).toLowerCase().includes('liquid') || String(f.cooling).toLowerCase().includes('aio')) {
    const userRad = parseCleanNumber(f.rad);
    if (!knownSpecs.maxAIO) {
      unknowns.push('RADIATOR / AIO SUPPORT UNKNOWN — VERIFY');
    } else if (maxAio === 0) fails.push("NO LIQUID COOLING SUPPORT");
    else if (!isNaN(userRad) && userRad > maxAio) fails.push(`RADIATOR LIMIT EXCEEDED ${maxAio}MM`);
    else {
      const fanThickness = f.radFan === 'slim' ? 15 : (f.radFan === 'thick' || f.radFan === '30mm' ? 30 : 25);
      const radThick = getExactSelectedComponentSpecs('aio')?.actual_thickness_mm || 27.2;
      const totalLiquidStack = radThick + fanThickness;
      if (c.top_fan_clearance && totalLiquidStack > parseInt(c.top_fan_clearance, 10)) {
        fails.push("COOLING STACK (" + totalLiquidStack + "MM) EXCEEDS AVAILABLE RADIATOR DEPTH");
      }
    }
  }

  const caseLength = c._dims[0];
  const caseWidth  = c._dims[1];
  const caseHeight = c._dims[2];

  if (caseLength > 0 && caseWidth > 0 && caseHeight > 0) {
    const sffBuffer = 5;
    const isVerticalOrientation = caseHeight > caseLength;

    if (c._layout === 'reference' || c._layout === 'apu') {
      const moboHorizontalLength = f.mobo === 'ATX' ? 244 : (f.mobo === 'MATX' ? 244 : 170);
      const moboVerticalHeight = f.mobo === 'ATX' ? 305 : (f.mobo === 'MATX' ? 244 : 170);

      if (isVerticalOrientation) {
        if (moboVerticalHeight > (caseHeight + sffBuffer)) {
          addAdvisory('GENERIC_MOBO_VERTICAL_ENVELOPE', `MOTHERBOARD HEIGHT (${moboVerticalHeight}MM) EXCEEDS THE EXTERIOR VERTICAL CASE HEIGHT (${caseHeight}MM) — VERIFY INTERNAL ORIENTATION`, { source: 'generic_chassis_envelope' });
        }

        if (f.cooling === 'liquid') {
          const fanThickness = f.radFan === 'slim' ? 15 : (f.radFan === 'thick' || f.radFan === '30mm' ? 30 : 25);
          const radThick = getExactSelectedComponentSpecs('aio')?.actual_thickness_mm || 27.2;
          const totalLiquidStackThickness = radThick + fanThickness;
          const physicalRadLengthNeeded = parseCleanNumber(f.rad) === 360 ? 395 : (parseCleanNumber(f.rad) === 280 ? 315 : (parseCleanNumber(f.rad) === 240 ? 275 : 155));

          if (physicalRadLengthNeeded > (caseHeight + sffBuffer) && physicalRadLengthNeeded > (caseLength + sffBuffer)) {
            addAdvisory('GENERIC_RAD_ENVELOPE', `SELECTED ${f.rad}MM RADIATOR FOOTPRINT (${physicalRadLengthNeeded}MM) EXCEEDS BOTH EXTERIOR CASE LENGTH/HEIGHT — VERIFY INTERNAL MOUNT ORIENTATION`, { source: 'generic_chassis_envelope' });
          }
          if (totalLiquidStackThickness > (caseWidth + sffBuffer)) {
            addAdvisory('GENERIC_RAD_STACK_WIDTH', `RADIATOR + FAN STACK (${totalLiquidStackThickness}MM) EXCEEDS EXTERIOR CASE WIDTH (${caseWidth}MM) — VERIFY INTERNAL MOUNT DEPTH`, { source: 'generic_chassis_envelope' });
          }
        }
      } else {
        if (moboVerticalHeight > (caseHeight + sffBuffer)) {
          addAdvisory('GENERIC_MOBO_ENVELOPE', `MOTHERBOARD HEIGHT (${moboVerticalHeight}MM) EXCEEDS EXTERIOR CASE HEIGHT (${caseHeight}MM) — VERIFY INTERNAL ORIENTATION`, { source: 'generic_chassis_envelope' });
        }
        if (f.mobo === 'MATX' && (f.psu === 'SFX' || f.psu === 'SFX-L') && f.cooling === 'liquid' && parseCleanNumber(f.rad) === 280) {
          if (c._volume < 20) addAdvisory('GENERIC_MATX_SFX_280_SUB20L', 'mATX + SFX/SFX-L + 280MM RADIATOR IN A SUB-20L CASE MAY REQUIRE A CONFLICTING PSU/RADIATOR ORIENTATION — VERIFY CASE-SPECIFIC MOUNTS', { source: 'generic_volume_layout', volume_l: c._volume });
        }
        if (f.psu === 'ATX') {
          const psuLengthDemand = 150;
          if ((moboHorizontalLength + psuLengthDemand + sffBuffer) > caseLength) addAdvisory('GENERIC_MOBO_ATX_PSU_ENVELOPE', `NOMINAL ${f.mobo} MOTHERBOARD + ATX PSU LENGTH (${moboHorizontalLength + psuLengthDemand}MM) EXCEEDS EXTERIOR CASE LENGTH (${caseLength}MM) — VERIFY INTERNAL ORIENTATION`, { source: 'generic_chassis_envelope' });
        } else if (f.psu === 'SFX-L' || f.psu === 'SFX') {
          const psuLengthDemand = f.psu === 'SFX-L' ? 130 : 100;
          if ((moboHorizontalLength + psuLengthDemand + sffBuffer) > caseLength && caseLength < 320) addAdvisory('GENERIC_MOBO_SFX_PSU_ENVELOPE', `NOMINAL ${f.mobo} MOTHERBOARD + ${f.psu} PSU LENGTH EXCEEDS EXTERIOR CASE LENGTH (${caseLength}MM) — VERIFY INTERNAL ORIENTATION`, { source: 'generic_chassis_envelope' });
        }
        if (f.cooling === 'liquid') {
          const fanThickness = f.radFan === 'slim' ? 15 : (f.radFan === 'thick' || f.radFan === '30mm' ? 30 : 25);
          const radThick = getExactSelectedComponentSpecs('aio')?.actual_thickness_mm || 27.2;
          const totalLiquidStackThickness = radThick + fanThickness;
          const physicalRadLengthNeeded = parseCleanNumber(f.rad) === 360 ? 395 : (parseCleanNumber(f.rad) === 280 ? 315 : (parseCleanNumber(f.rad) === 240 ? 275 : 155));

          if (physicalRadLengthNeeded > (caseLength + sffBuffer)) addAdvisory('GENERIC_RAD_HORIZONTAL_ENVELOPE', `SELECTED ${f.rad}MM RADIATOR FOOTPRINT (${physicalRadLengthNeeded}MM) EXCEEDS EXTERIOR CASE LENGTH (${caseLength}MM) — VERIFY INTERNAL MOUNT ORIENTATION`, { source: 'generic_chassis_envelope' });
          if (totalLiquidStackThickness > (caseWidth + sffBuffer) && caseWidth < 140) addAdvisory('GENERIC_RAD_HORIZONTAL_WIDTH', `RADIATOR + FAN STACK (${totalLiquidStackThickness}MM) EXCEEDS EXTERIOR CASE WIDTH — VERIFY INTERNAL MOUNT DEPTH`, { source: 'generic_chassis_envelope' });
        }
      }

      if (f.cooling === 'air' && f.cpuH > (caseWidth + sffBuffer)) {
        addAdvisory('GENERIC_CPU_CASE_WIDTH', `CPU AIR COOLER HEIGHT (${f.cpuH}MM) EXCEEDS EXTERIOR CASE WIDTH (${caseWidth}MM) — VERIFY INTERNAL COOLER ORIENTATION`, { source: 'generic_chassis_envelope' });
      }
    }

    if (c._layout === 'sandwich') {
      if (f.cooling === 'liquid') {
        const liquidFanThickness = f.radFan === 'slim' ? 15 : (f.radFan === 'thick' || f.radFan === '30mm' ? 30 : 25);
        const radThick = getExactSelectedComponentSpecs('aio')?.actual_thickness_mm || 27.2;
        const liquidStackHeight = radThick + liquidFanThickness;

        if (isVerticalOrientation) {
          if (parseCleanNumber(f.rad) > 280) {
            addAdvisory('GENERIC_SANDWICH_RAD_SIZE', '360MM+ RADIATORS ARE UNCOMMON IN SANDWICH LAYOUTS — VERIFY CASE-SPECIFIC RADIATOR SUPPORT', { source: 'generic_layout' });
          }
        } else {
          if ((170 + liquidStackHeight + sffBuffer) > caseHeight) {
            addAdvisory('GENERIC_SANDWICH_RAD_STACK', `NOMINAL AIO RADIATOR STACK (${liquidStackHeight}MM) MAY OVERLAP THE GENERIC SANDWICH-LAYOUT ENVELOPE — VERIFY CASE-SPECIFIC MOUNTING`, { source: 'generic_layout' });
          }
        }
      }
    }

    if (c._layout === 'console') {
      if (f.cpuH + sffBuffer > caseWidth) addAdvisory('GENERIC_CONSOLE_CPU_WIDTH', `CPU COOLER HEIGHT (${f.cpuH}MM) EXCEEDS EXTERIOR CONSOLE CASE WIDTH (${caseWidth}MM) — VERIFY INTERNAL ORIENTATION`, { source: 'generic_layout' });
      if (!c._isAPU && (f.gpuT + sffBuffer) > caseWidth) addAdvisory('GENERIC_CONSOLE_GPU_WIDTH', 'GPU THICKNESS EXCEEDS THE EXTERIOR CONSOLE CASE WIDTH — VERIFY INTERNAL GPU ORIENTATION', { source: 'generic_layout' });
      if (f.cooling === 'liquid' && parseCleanNumber(f.rad) >= 240) addAdvisory('GENERIC_CONSOLE_RAD_SIZE', '240MM+ RADIATORS MAY BE DIFFICULT IN CONSOLE LAYOUTS — VERIFY CASE-SPECIFIC RADIATOR SUPPORT', { source: 'generic_layout' });
    }

    if (!c._isAPU) {
      const pcieWiringTurnRadius = 7;
      const totalGpuHeightNeeded = f.gpuH + pcieWiringTurnRadius;

      if (c._layout === 'reference' || c._layout === 'apu') {
        if (totalGpuHeightNeeded > (caseWidth + sffBuffer)) {
          addAdvisory('GENERIC_GPU_CABLE_SIDE', 'GPU HEIGHT + ESTIMATED PCIE CABLE TURN RADIUS EXCEEDS EXTERIOR CASE WIDTH — VERIFY SIDE-PANEL CABLE CLEARANCE', { source: 'generic_cable_geometry' });
        }
      } else if (c._layout === 'sandwich' || c._layout === 'console') {
        if (caseHeight > 0 && totalGpuHeightNeeded > (caseHeight + sffBuffer)) {
          addAdvisory('GENERIC_GPU_CABLE_TOP', 'GPU HEIGHT + ESTIMATED PCIE CABLE TURN RADIUS EXCEEDS EXTERIOR CASE HEIGHT — VERIFY TOP-PANEL / RADIATOR CABLE CLEARANCE', { source: 'generic_cable_geometry' });
        }
      }
    }
  }

  if (c._specText) {
    STRUCTURAL_RULES.forEach(rule => {
      if (rule.condition(f)) {
        const tokensMatch = rule.keywords.every(group => group.some(phrase => c._specText.includes(phrase)));
        if (tokensMatch) addAdvisory('LEGACY_STRUCTURAL_TEXT_RULE', rule.error + ' — VERIFY AGAINST THE STRUCTURED CASE RULESET', { source: 'legacy_spec_text_rule' });
      }
    });
  }

  const finalSpecs = {
    ...specs,
    maxGPU: maxGpuLen,
    maxGPUT: maxThick,
    maxGPUH: maxGpuHeight,
    maxCPU: maxCpu,
    maxAIO: maxAio,
    maxPSU: constrainedSpecs.maxPSU ?? specs.maxPSU,
    known: { ...(constrainedSpecs.known || specs.known || {}) }
  };

  unknowns = [...new Set(unknowns)];
  warns = [...new Set(warns)];
  fails = [...new Set(fails)];
  const seenAdvisories = new Set();
  advisories = advisories.filter(a => {
    const key = `${a.code || ''}|${a.message || ''}`;
    if (seenAdvisories.has(key)) return false;
    seenAdvisories.add(key);
    return true;
  });
  return { fails, warns, unknowns, advisories, spineStep, activeSpecs: finalSpecs, fits: fails.length === 0 }; 
}

export function syncCaseLayoutFilter(c) {
  if (!c) return 'all';
  const targetLayout = c._isAPU ? 'apu' : (c._layout || 'all');
  state.activeLayout = targetLayout;

  const layoutBtn = document.querySelector(`#layoutFilterGroup .segment-btn[data-filter="${targetLayout}"]`);
  if (layoutBtn) {
    document.querySelectorAll('#layoutFilterGroup .segment-btn').forEach(b => {
      b.classList.remove('active');
      b.setAttribute('aria-pressed', 'false');
      b.setAttribute('aria-checked', 'false');
    });
    layoutBtn.classList.add('active');
    layoutBtn.setAttribute('aria-pressed', 'true');
    layoutBtn.setAttribute('aria-checked', 'true');
  }

  return targetLayout;
}

export function applyCaseCompatibleDefaults(c) {
  if (!c) return;

  // A documented/imported spine step belongs to one exact physical case state.
  // Ordinary case navigation intentionally starts from that case's compatible defaults.
  state.portableFixedStep = null;

  // Case navigation intentionally creates a compatible default build. Any exact
  // component identity from the previous case must therefore be cleared rather
  // than left attached to the new case-derived dimensions.
  clearAllSelectedComponents();
  ['#gpuCombobox .combobox-input', '#cpuCoolerCombobox .combobox-input', '#aioCoolerCombobox .combobox-input', '#aioSearchCombobox .combobox-input', '#moboCombobox .combobox-input']
    .forEach(selector => { const input = document.querySelector(selector); if (input) input.value = ''; });

  const dom = {
    vol: document.getElementById('maxVolume'),
    gpuL: document.getElementById('gpuLength'),
    gpuT: document.getElementById('gpuThickness'),
    gpuH: document.getElementById('gpuHeight'),
    cpuH: document.getElementById('cpuHeight'),
    moboVal: document.getElementById('moboType'),
    psuVal: document.getElementById('psuType')
  };

  syncCaseLayoutFilter(c);

  if (Array.isArray(c._moboList) && c._moboList.length > 0) {
    let preferredMobo = c._moboList.includes('ITX') ? 'ITX' : (c._moboList.includes('MATX') ? 'mATX' : c._moboList[0]);
    if (preferredMobo === 'MATX') preferredMobo = 'mATX';
    const moboBtn = document.querySelector(`#moboSegmented .segment-btn[data-value="${preferredMobo}"]`);
    if (moboBtn) {
      document.querySelectorAll('#moboSegmented .segment-btn').forEach(b => {
        b.classList.remove('active'); b.setAttribute('aria-pressed', 'false'); b.setAttribute('aria-checked', 'false');
      });
      moboBtn.classList.add('active'); moboBtn.setAttribute('aria-pressed', 'true'); moboBtn.setAttribute('aria-checked', 'true');
      if (dom.moboVal) dom.moboVal.value = preferredMobo.toUpperCase();
    }
  }

  if (Array.isArray(c._psuList) && c._psuList.length > 0) {
    let preferredPsu = c._psuList.includes('SFX') ? 'SFX' : (c._psuList.includes('SFX-L') ? 'SFX-L' : (c._psuList.includes('FLEX') ? 'FLEX' : c._psuList[0]));
    const psuBtn = [...document.querySelectorAll('#psuSegmented .segment-btn')].find(b => b.getAttribute('data-value')?.toUpperCase() === preferredPsu);
    if (psuBtn) {
      document.querySelectorAll('#psuSegmented .segment-btn').forEach(b => {
        b.classList.remove('active'); b.setAttribute('aria-pressed', 'false'); b.setAttribute('aria-checked', 'false');
      });
      psuBtn.classList.add('active'); psuBtn.setAttribute('aria-pressed', 'true'); psuBtn.setAttribute('aria-checked', 'true');
      if (dom.psuVal) dom.psuVal.value = psuBtn.getAttribute('data-value');
    }
  }

  if (dom.vol && c._volume > 0 && parseInt(dom.vol.value, 10) < Math.ceil(c._volume)) {
    dom.vol.value = Math.min(35, Math.ceil(c._volume));
  }

  const activeModeId = getCaseModeSelection(c, Array.isArray(c.modes) && c.modes.length ? c.modes[0].mode_id : 'default');
  const effSpecs = getEffectiveSpecs(c, activeModeId);

  const caseMaxPcie = c._known?.pcie && c._pcie > 0 ? Number(c._pcie) : null;

  if (!c._isAPU) {
    if (effSpecs.maxGPU > 0 && dom.gpuL) dom.gpuL.value = effSpecs.maxGPU;
    if (effSpecs.maxGPUT > 0 && dom.gpuT) {
      dom.gpuT.value = effSpecs.maxGPUT;
      syncPcieSlotsFromThickness(effSpecs.maxGPUT, caseMaxPcie, { clampToCase: true });
    }
    if (effSpecs.maxGPUH > 0 && dom.gpuH) dom.gpuH.value = effSpecs.maxGPUH;
  }

  if (effSpecs.maxCPU > 0 && effSpecs.maxCPU < 9999 && dom.cpuH) {
    dom.cpuH.value = effSpecs.maxCPU;
  }

  updateSliderBadges();
}

export async function loadApplicationData(dom, evaluateCompatibility, deps = {}) {
  const {
    populateHardwareDropdowns, populateCaseSearchDropdown, setupLazyLoadingEngine,
    parseIncomingComparisonRoute, parseIncomingUrlParams, processUrlHashActions
  } = deps;

  const hydrateStarted = runtimeNow();
  try {
    let meta = null;

    // Meta is intentionally refreshed so the UI can show the real catalog sync time.
    // The large versioned payloads themselves rely on the browser HTTP cache rather
    // than synchronous localStorage JSON parse/stringify on the main thread.
    try {
      const metaStarted = runtimeNow();
      const metaResponse = await fetch('/meta.json', { cache: 'no-cache' });
      if (metaResponse.ok) {
        meta = await metaResponse.json();
        const syncDate = new Date(meta.lastSynced);
        const statusNode = document.getElementById('sync-status');
        if (statusNode && !Number.isNaN(syncDate.getTime())) {
          statusNode.textContent = `Data Last Synced: ${syncDate.toLocaleDateString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}`;
        }
      }
      debugPerformanceMetric('hydrate.meta', metaStarted);
    } catch (metaErr) {
      console.warn('meta.json sync skipped:', metaErr);
    }

    const casesVersionSuffix = meta?.lite_version ? `?v=${encodeURIComponent(meta.lite_version)}` : '';
    const hardwareVersionSuffix = meta?.hardware_version ? `?v=${encodeURIComponent(meta.hardware_version)}` : '';
    const structuredVersion = meta?.structured_version || meta?.lite_version || '';
    const payloadStarted = runtimeNow();

    // All three catalog resources are independent once meta has supplied their versions.
    // Start them together so structured rules do not add another network round trip before
    // the first deterministic evaluation.
    const [resCases, resHw, structuredDataset] = await Promise.all([
      fetch(`/cases.php${casesVersionSuffix}`, { cache: meta?.lite_version ? 'default' : 'no-cache' }),
      fetch(`/hardware.php${hardwareVersionSuffix}`, { cache: meta?.hardware_version ? 'default' : 'no-cache' }),
      fetchCasesStructuredDataset(structuredVersion)
    ]);
    if (!resCases.ok) throw new Error(`HTTP ${resCases.status} loading cases.php`);
    if (!resHw.ok) throw new Error(`HTTP ${resHw.status} loading hardware.php`);

    const [casesPayload, hardwarePayload] = await Promise.all([resCases.json(), resHw.json()]);
    debugPerformanceMetric('hydrate.catalog-payloads', payloadStarted, {
      casesVersion: meta?.lite_version || 'uncached',
      hardwareVersion: meta?.hardware_version || 'uncached',
      structuredVersion: structuredVersion || 'uncached'
    });

    // Remove obsolete bulk localStorage entries outside the critical hydration path.
    scheduleIdleTask(() => {
      try {
        ['sff_cases_data', 'sff_hardware_data', 'sff_ver_cases', 'sff_ver_hw'].forEach(key => localStorage.removeItem(key));
      } catch (error) {
        debugRuntimeIssue('Legacy Catalog Cache Cleanup', error);
      }
    });

    const enrichStarted = runtimeNow();
    const rawCases = casesPayload.cases || (Array.isArray(casesPayload) ? casesPayload : []);
    state.fetchedCasesGlobal = rawCases.filter(c => c && c.name).map(enrichCase);
    window.fetchedCasesGlobal = state.fetchedCasesGlobal;

    applyCasesStructuredDataset(structuredDataset);
    rebuildCaseLookupIndexes(state.fetchedCasesGlobal);
    clearCardPool();
    debugPerformanceMetric('hydrate.case-enrichment', enrichStarted, { cases: state.fetchedCasesGlobal.length });

    const uiStarted = runtimeNow();
    const hardwareData = hardwarePayload.hardware || hardwarePayload;
    if (typeof populateCaseSearchDropdown === 'function') populateCaseSearchDropdown(state.fetchedCasesGlobal);
    if (typeof populateHardwareDropdowns === 'function') populateHardwareDropdowns(hardwareData);

    // Backend universal IDs are indexed only after both catalogs are present. Route/build
    // restoration below can then resolve a stable ID without recreating identity in JS.
    rebuildCatalogIdentityIndex(
      state.fetchedCasesGlobal,
      hardwareData,
      hardwarePayload.identity || casesPayload.identity || meta?.identity || meta?.catalog_identity || null
    );

    if (typeof setupLazyLoadingEngine === 'function') setupLazyLoadingEngine();
    if (typeof parseIncomingComparisonRoute === 'function') parseIncomingComparisonRoute();

    let routeAlreadyEvaluated = false;
    if (typeof parseIncomingUrlParams === 'function') {
      routeAlreadyEvaluated = !!(await parseIncomingUrlParams(dom, evaluateCompatibility, applyCaseCompatibleDefaults, updateSliderBadges, null));
    }

    // One deterministic evaluation is enough for first paint. Route restoration
    // may already have evaluated while its restore guard was active; do not repeat it.
    // Edge-AI/GNN initialize lazily only when active-case inference needs them.
    if (!routeAlreadyEvaluated) evaluateCompatibility();

    if (state.selectedCaseIdx !== null && typeof processUrlHashActions === 'function') {
      processUrlHashActions(state.selectedCaseIdx);
    }

    debugPerformanceMetric('hydrate.ui-bindings', uiStarted);
    debugPerformanceMetric('hydrate.total', hydrateStarted, { cases: state.fetchedCasesGlobal.length });
  } catch (e) {
    console.error('Hydration Error:', e);
    if (dom.list) dom.list.innerHTML = `<div style="padding:24px;text-align:center;color:#ef4444;" class="font-mono">CRITICAL ERROR LOADING DATA LAYER: ${e.message}</div>`;
  }
}
